import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Search, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from '@/components/ui/input-group'

interface SearchInputProps {
  /** The search text currently applied to the list. */
  value: string
  /** Called with the new text once the user stops typing. */
  onChange: (value: string) => void
  /** Overrides the default "Search" placeholder. */
  placeholder?: string
  /** How long to wait after the last keystroke before searching, in ms. */
  delay?: number
  className?: string
}

/**
 * Search field for the top of a list. Keeps what the user types locally and
 * only reports it once they pause, so a list is not refetched per keystroke.
 */
export function SearchInput({
  value,
  onChange,
  placeholder,
  delay = 300,
  className,
}: SearchInputProps) {
  const { t } = useTranslation()
  const [text, setText] = useState(value)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Follow the applied value when the list resets or restores its search.
  useEffect(() => {
    setText(value)
  }, [value])

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current) }, [])

  const emit = (next: string, immediate = false) => {
    setText(next)
    if (timer.current) clearTimeout(timer.current)
    if (immediate) {
      onChange(next)
      return
    }
    timer.current = setTimeout(() => onChange(next), delay)
  }

  return (
    <InputGroup className={cn('h-8 w-56', className)}>
      <InputGroupAddon>
        <Search />
      </InputGroupAddon>
      <InputGroupInput
        type="search"
        className="[&::-webkit-search-cancel-button]:appearance-none"
        value={text}
        placeholder={placeholder ?? t('common.search')}
        aria-label={placeholder ?? t('common.search')}
        onChange={(e) => emit(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') emit(text, true)
          if (e.key === 'Escape') emit('', true)
        }}
      />
      {text && (
        <InputGroupAddon align="inline-end">
          <InputGroupButton
            size="icon-xs"
            aria-label={t('common.clearSearch')}
            onClick={() => emit('', true)}
          >
            <X />
          </InputGroupButton>
        </InputGroupAddon>
      )}
    </InputGroup>
  )
}
